import { useState, useEffect, useCallback, useRef } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'
import { SortDirection as SortDirectionEnum } from '@/enums/sort-direction.enum'
import { ToolStatus } from '@/enums/tool-status.enum'
import type { Paginated, PaginationParams } from '@/types'

export interface Tool {
  part_number: string
  chinese_name: string
  description: string
  quantity: number
  available_quantity: number
  borrowed_quantity: number
  missing_quantity: number
  id: number
  drawer_id: number
  image_key: string
  image_url: string
  is_active: boolean
  created_at: string
  updated_at: string
  [key: string]: unknown
}

export type SortOrder = SortDirectionEnum

export interface GetToolsQuery extends PaginationParams {
  sort_order?: Nillable<SortOrder>
  sort_by?: Nillable<string>
  search?: Nillable<string>
  status?: Nillable<ToolStatus>
}

type UseGetToolsReturn = Paginated<Tool> & {
  isLoading: boolean
  error: Nullable<string>
  refetch: () => Promise<void>
}

export function useGetTools(
  drawerId: StrOrNum,
  query?: GetToolsQuery
): UseGetToolsReturn {
  const [tools, setTools] = useState<Tool[]>([])
  const [pagination, setPagination] = useState({
    page: query?.page ?? 1,
    page_size: query?.page_size ?? 10,
    total: 0,
    total_pages: 0,
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  useEffect(() => {
    setTools([])
    setPagination({
      page: query?.page ?? 1,
      page_size: query?.page_size ?? 10,
      total: 0,
      total_pages: 0,
    })
  }, [drawerId])

  const fetchTools = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const search = query?.search?.trim()
      const status = query?.status?.trim()
      const sort_by = query?.sort_by?.trim()
      const sort_order = query?.sort_order ?? undefined
      const response = await apiClient.get<Paginated<Tool>>(
        `/drawers/${drawerId}/tools`,
        {
          params: {
            page: query?.page ?? 1,
            page_size: query?.page_size ?? 10,
            ...(search ? { search } : {}),
            ...(status ? { status } : {}),
            ...(sort_by ? { sort_by } : {}),
            ...(sort_order ? { sort_order } : {}),
          },
        }
      )
      setTools(response.data.items)
      setPagination({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total,
        total_pages: response.data.total_pages,
      })
    } catch (err) {
      setError(handleErrorRef.current(err))
      setTools([])
      setPagination({
        page: query?.page ?? 1,
        page_size: query?.page_size ?? 10,
        total: 0,
        total_pages: 0,
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    drawerId,
    query?.page,
    query?.page_size,
    query?.search,
    query?.status,
    query?.sort_by,
    query?.sort_order,
  ])

  useEffect(() => {
    fetchTools()
  }, [fetchTools])

  return {
    items: tools,
    page: pagination.page,
    page_size: pagination.page_size,
    total: pagination.total,
    total_pages: pagination.total_pages,
    isLoading,
    error,
    refetch: fetchTools,
  }
}
